import { z } from 'zod'
import { emailSchema } from '../validators/email.validator.js'

/** A company name as a person types one: trimmed, never blank. */
const companyNameSchema = z.string().trim().min(1, 'Enter the company’s name.').max(120, 'Keep the company’s name to 120 characters.')

/**
 * One person to convert from a client into a planner (docs/planner-vendors.md,
 * "The conversion task"). Rows naming the same firm join one company, the first
 * listed its owner; a row with no firm gets a company named for the person.
 */
export const plannerConversionRowSchema = z
  .object({
    email: emailSchema,
    firm: companyNameSchema.optional(),
  })
  .strict()

export type PlannerConversionRow = z.infer<typeof plannerConversionRowSchema>

/**
 * What the one-time task sends the API. Everything is optional: `{}` is a dry
 * run over everyone the API finds, and `{ "apply": true }` converts them.
 *
 * - `rows` — who to convert, when not everyone found: a list, each row naming
 *   its firm or not. Without it, the API finds them (see `candidateEmails`).
 * - `exclude` — people found who are not planners, left out.
 * - `only` — apply to that one person, the rest read for the conflicts it meets.
 */
export const plannerConversionInputSchema = z
  .object({
    rows: z.array(plannerConversionRowSchema).min(1).max(200).optional(),
    apply: z.boolean().default(false),
    only: emailSchema.optional(),
    exclude: z.array(emailSchema).max(200).default([]),
  })
  .strict()

export type PlannerConversionInput = z.infer<typeof plannerConversionInputSchema>
/** What the task may be sent, before defaults: `{}` included. */
export type PlannerConversionPayload = z.input<typeof plannerConversionInputSchema>

/**
 * A venue manager marking one of an event's clients as a planner (decision 32).
 * The company is named for the person unless the manager names it.
 */
export const clientToPlannerInputSchema = z
  .object({
    companyName: companyNameSchema.optional(),
  })
  .strict()

export type ClientToPlannerInput = z.infer<typeof clientToPlannerInputSchema>
